import { useEffect, useRef } from 'react';
import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import resolveScrollOverflow from './tabs/resolveScrollOverflow.js';

/**
 * Switches between views of the same subject. `items` is [{ value, label, panel }].
 * When the tabs do not fit the width they scroll sideways: the list marks which edge has more
 * (`data-overflow`, drawn as a fade) and keeps the active tab in view.
 */
const Tabs = ({ items, value, defaultValue, onValueChange, label }) => {
  const listRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return undefined;
    }
    const update = () => {
      const overflow = resolveScrollOverflow({ scrollLeft: list.scrollLeft, clientWidth: list.clientWidth, scrollWidth: list.scrollWidth });
      if (overflow) {
        list.dataset.overflow = overflow;
      } else {
        delete list.dataset.overflow;
      }
    };
    update();
    list.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(list);
    return () => {
      list.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    const active = listRef.current?.querySelector('[data-active]');
    active?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  return (
    <BaseTabs.Root className="comp-tabs" value={value} defaultValue={defaultValue ?? items[0]?.value} onValueChange={onValueChange}>
      <BaseTabs.List ref={listRef} className="comp-tabs__list" aria-label={label}>
        {items.map((item) => (
          <BaseTabs.Tab key={item.value} value={item.value} className="comp-tabs__tab">
            {item.label}
          </BaseTabs.Tab>
        ))}
        <BaseTabs.Indicator className="comp-tabs__indicator" />
      </BaseTabs.List>
      {items.map((item) => (
        <BaseTabs.Panel key={item.value} value={item.value} className="comp-tabs__panel">
          {item.panel}
        </BaseTabs.Panel>
      ))}
    </BaseTabs.Root>
  );
};

export default Tabs;
